import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Card, CardContent } from '@mdvip/shared/components/ui/card';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';

interface MembershipBenefitsProps {
  className?: string;
}

/**
 * Payment Center "Benefits of membership" marketing grid. Content is hardcoded placeholder copy —
 * a real content-storage pattern hasn't been decided yet, and the "Learn more" links are
 * non-functional until real destinations are wired up.
 */
export function MembershipBenefits({ className }: MembershipBenefitsProps) {
  const { t } = useTranslation('members');

  const benefits: { icon: IconName; title: string; description: string }[] = [
    {
      icon: 'stethoscope',
      title: t('payments.benefits.medicalCentersOfExcellence.title'),
      description: t('payments.benefits.medicalCentersOfExcellence.description'),
    },
    {
      icon: 'plane',
      title: t('payments.benefits.travelAdvantages.title'),
      description: t('payments.benefits.travelAdvantages.description'),
    },
    {
      icon: 'utensils',
      title: t('payments.benefits.recipesAndMealPlans.title'),
      description: t('payments.benefits.recipesAndMealPlans.description'),
    },
    {
      icon: 'dumbbell',
      title: t('payments.benefits.workoutsAndFitnessPlans.title'),
      description: t('payments.benefits.workoutsAndFitnessPlans.description'),
    },
  ];

  return (
    <section className={cn('mt-8 border-t pt-8', className)} data-slot="membership-benefits">
      <h3 className="mb-6 text-h3 font-semibold text-foreground">{t('payments.benefits.sectionTitle')}</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {benefits.map((benefit) => (
          <Card key={benefit.title} className="h-full transition-shadow hover:shadow-md">
            <CardContent className="flex h-full flex-col">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border bg-muted text-foreground">
                <Icon name={benefit.icon} size="lg" />
              </div>
              <div className="mb-2 text-sm font-semibold leading-snug text-foreground">{benefit.title}</div>
              <p className="mb-4 flex-1 text-xs leading-relaxed text-muted-foreground">{benefit.description}</p>
              {/* Not a real link yet: real destinations aren't decided, so this is an inert
                  button styled as a link rather than an <a> with no href to navigate to. */}
              <button
                type="button"
                className="inline-flex items-center gap-1 self-start whitespace-nowrap text-xs font-medium text-primary underline-offset-4 hover:underline"
              >
                {t('payments.benefits.learnMore')}
                <Icon name="chevronRight" size="xs" />
              </button>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
